import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { authenticatedFetch, getCurrentUser } from '../../../utils/auth';
import Button from '../../../components/ui/Button';
import Card from '../../../components/ui/Card';
import Modal from '../../../components/ui/Modal';
import PersonForm from '../../../components/PersonForm';
import SearchInput from '../../../components/SearchInput';

// Types
interface Admin {
    id: string;
    username: string;
    role: 'ADMIN' | 'VIEWER';
}

interface User {
    id: string;
    email: string;
    altemail?: string;
    person: Person[];
}

interface Person {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    phone?: string;
    address: string;
    houseNumber: string;
    city: string;
    psc: string;
    rodCislo?: string;
    isBernolakova: boolean;
    type: 'PARENT' | 'ADULT' | 'KID';
    gender?: 'MALE' | 'FEMALE';
    userId: string;
    schoolyear?: {
        id: string;
        name: string;
        order: number;
        type: 'PRESCHOOL' | 'PRIMARY' | 'HIGHSCHOOL';
    };
    prihlaska?: Array<{
        id: string;
        status: string;
    }>;
}



interface EditPersonModalProps {
    person: Person | null;
    isOpen: boolean;
    onClose: () => void;
    onSave: (personData: Partial<Person>) => void;
}

interface CreateAdminModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (username: string, password: string, role: 'ADMIN' | 'VIEWER') => void;
}

const CreateAdminModal: React.FC<CreateAdminModalProps> = ({ isOpen, onClose, onSave }) => {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [role, setRole] = useState<'ADMIN' | 'VIEWER'>('ADMIN');

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        if (!username.trim()) {
            alert('Zadajte meno používateľa');
            return;
        }

        if (password.length < 6) {
            alert('Heslo musí mať aspoň 6 znakov');
            return;
        }

        if (password !== confirmPassword) {
            alert('Heslá sa nezhodujú');
            return;
        }

        onSave(username.trim(), password, role);

        // Reset form
        setUsername('');
        setPassword('');
        setConfirmPassword('');
        setRole('ADMIN');
    };

    const handleClose = () => {
        setUsername('');
        setPassword('');
        setConfirmPassword('');
        setRole('ADMIN');
        onClose();
    };

    if (!isOpen) return null;

    return (
        <Modal isOpen={isOpen} onClose={handleClose} title="Vytvoriť nového administrátora">
            <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Meno používateľa
                    </label>
                    <input
                        type="text"
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                        autoFocus
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Heslo
                    </label>
                    <input
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                        minLength={6}
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Potvrdiť heslo
                    </label>
                    <input
                        type="password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                        minLength={6}
                    />
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                    <label className="text-sm text-gray-700">Prístup
                        <select value={role} onChange={event => setRole(event.target.value as 'ADMIN' | 'VIEWER')} className="block border border-gray-300 rounded p-2 mt-1">
                            <option value="ADMIN">Administrátor</option>
                            <option value="VIEWER">Bernolákova (iba čítanie)</option>
                        </select>
                    </label>
                </div>
                <div className="flex justify-end space-x-3 pt-4">
                    <Button type="button" variant="secondary" onClick={handleClose}>
                        Zrušiť
                    </Button>
                    <Button type="submit" variant="primary">
                        Vytvoriť
                    </Button>
                </div>
            </form>
        </Modal>
    );
};

const EditPersonModal: React.FC<EditPersonModalProps> = ({ person, isOpen, onClose, onSave }) => {
    if (!isOpen || !person) return null;

    const handlePersonSave = async (personData: any) => {
        await onSave(personData);
        onClose();
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} title="Upraviť osobu">
            <div className="max-w-4xl mx-auto">
                <PersonForm
                    initialData={person}
                    userEmail={person.email}
                    onSubmit={handlePersonSave}
                    onCancel={onClose}
                    isEmbedded={true}
                    title="Upraviť údaje osoby"
                    autosave={false}
                />
            </div>
        </Modal>
    );
};

const UserManagement: React.FC = () => {
    const navigate = useNavigate();
    const [admins, setAdmins] = useState<Admin[]>([]);
    const [users, setUsers] = useState<User[]>([]);
    const [loading, setLoading] = useState(true);
    const [editingPerson, setEditingPerson] = useState<Person | null>(null);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isCreateAdminModalOpen, setIsCreateAdminModalOpen] = useState(false);
    const [isCreateUserModalOpen, setIsCreateUserModalOpen] = useState(false);
    const [isCreatePersonModalOpen, setIsCreatePersonModalOpen] = useState(false);
    const [createPersonUserId, setCreatePersonUserId] = useState<string | null>(null);
    const [isSelectPersonTypeOpen, setIsSelectPersonTypeOpen] = useState(false);
    const [selectedPersonType, setSelectedPersonType] = useState<'PARENT' | 'ADULT' | 'KID' | null>(null);
    const [expandedUsers, setExpandedUsers] = useState<Set<string>>(new Set());
    const [creatingPrihlaskaFor, setCreatingPrihlaskaFor] = useState<string | null>(null);

    const [currentAdminLoggedIn, setCurrentAdminLoggedIn] = useState<string | null>(getCurrentUser()?.id);

    // Search and filter states
    const [searchQuery, setSearchQuery] = useState('');
    const [personTypeFilter, setPersonTypeFilter] = useState<'ALL' | 'PARENT' | 'ADULT' | 'KID'>('ALL');
    const [userStatusFilter, setUserStatusFilter] = useState<'ALL' | 'WITH_PERSONS' | 'WITHOUT_PERSONS'>('ALL');

    // Pagination states
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);

    // Fetch data
    const fetchData = async () => {
        try {
            setLoading(true);
            const [adminsResponse, usersResponse] = await Promise.all([
                authenticatedFetch(`${process.env.REACT_APP_PATH}/api/admin/admins`),
                authenticatedFetch(`${process.env.REACT_APP_PATH}/api/admin/users`)
            ]);

            if (adminsResponse.ok) {
                const adminsData = await adminsResponse.json();
                setAdmins(adminsData.data);
            }

            if (usersResponse.ok) {
                const usersData = await usersResponse.json();
                const usersList = usersData.data as User[];

                // Sort rules:
                // - Users that have no persons (person.length === 0) go last
                // - Users with persons are sorted by the first person's lastName, then firstName, then email
                // - Users without persons are sorted by their email address
                const usersSorted = [...usersList].sort((a, b) => {
                    const aHasPerson = Array.isArray(a.person) && a.person.length > 0;
                    const bHasPerson = Array.isArray(b.person) && b.person.length > 0;

                    if (!aHasPerson && !bHasPerson) {
                        return a.email.localeCompare(b.email);
                    }

                    if (!aHasPerson) return 1; // a goes after b
                    if (!bHasPerson) return -1; // b goes after a

                    const aLast = a.person[0]?.lastName ?? '';
                    const bLast = b.person[0]?.lastName ?? '';
                    const lastCmp = aLast.localeCompare(bLast);
                    if (lastCmp !== 0) return lastCmp;

                    const aFirst = a.person[0]?.firstName ?? '';
                    const bFirst = b.person[0]?.firstName ?? '';
                    const firstCmp = aFirst.localeCompare(bFirst);
                    if (firstCmp !== 0) return firstCmp;

                    // Fallback to email to ensure deterministic order
                    return a.email.localeCompare(b.email);
                });

                setUsers(usersSorted);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    // Filter and search users
    const filteredUsers = useMemo(() => {
        let filtered = [...users];

        // Apply search query
        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase().trim();
            filtered = filtered.filter(user => {
                // Search in user email
                if (user.email.toLowerCase().includes(query)) return true;
                if (user.altemail?.toLowerCase().includes(query)) return true;

                // Search in person names and emails
                return user.person.some(person =>
                    person.firstName.toLowerCase().includes(query) ||
                    person.lastName.toLowerCase().includes(query) ||
                    person.email.toLowerCase().includes(query) ||
                    `${person.firstName} ${person.lastName}`.toLowerCase().includes(query) ||
                    `${person.lastName} ${person.firstName}`.toLowerCase().includes(query)
                );
            });
        }

        // Apply user status filter
        if (userStatusFilter === 'WITH_PERSONS') {
            filtered = filtered.filter(user => user.person.length > 0);
        } else if (userStatusFilter === 'WITHOUT_PERSONS') {
            filtered = filtered.filter(user => user.person.length === 0);
        }

        // Apply person type filter
        if (personTypeFilter !== 'ALL') {
            filtered = filtered.filter(user =>
                user.person.some(person => person.type === personTypeFilter)
            );
        }

        return filtered;
    }, [users, searchQuery, userStatusFilter, personTypeFilter]);

    // Pagination calculations
    const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedUsers = filteredUsers.slice(startIndex, endIndex);

    // Reset to first page when filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [searchQuery, userStatusFilter, personTypeFilter]);

    // Clear filters function
    const clearFilters = () => {
        setSearchQuery('');
        setPersonTypeFilter('ALL');
        setUserStatusFilter('ALL');
        setCurrentPage(1);
    };

    // Pagination helpers
    const goToPage = (page: number) => {
        setCurrentPage(Math.max(1, Math.min(page, totalPages)));
    };

    const goToNextPage = () => {
        if (currentPage < totalPages) {
            setCurrentPage(currentPage + 1);
        }
    };

    const goToPrevPage = () => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1);
        }
    };

    // Delete admin
    const handleDeleteAdmin = async (adminId: string) => {
        if (!window.confirm('Naozaj chcete odstrániť tohto administrátora?')) return;

        try {
            const response = await authenticatedFetch(
                `${process.env.REACT_APP_PATH}/api/admin/admin/${adminId}`,
                { method: 'DELETE' }
            );

            if (response.ok) {
                setAdmins(prev => prev.filter(admin => admin.id !== adminId));
            } else {
                alert('Chyba pri odstraňovaní administrátora');
            }
        } catch (error) {
            console.error('Error deleting admin:', error);
            alert('Chyba pri odstraňovaní administrátora');
        }
    };

    // Create admin
    const handleCreateAdmin = async (username: string, password: string, role: 'ADMIN' | 'VIEWER') => {
        try {
            const response = await authenticatedFetch(
                `${process.env.REACT_APP_PATH}/api/admin/admin`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ username, password, role })
                }
            );

            if (response.ok) {
                const result = await response.json();
                setAdmins(prev => [...prev, result.data]);
                setIsCreateAdminModalOpen(false);
                alert('Administrátor bol úspešne vytvorený');
            } else {
                const error = await response.json();
                alert(error.error || 'Chyba pri vytváraní administrátora');
            }
        } catch (error) {
            console.error('Error creating admin:', error);
            alert('Chyba pri vytváraní administrátora');
        }
    };

    // Toggle user expansion
    const toggleUserExpansion = (userId: string) => {
        setExpandedUsers(prev => {
            const newSet = new Set(prev);
            if (newSet.has(userId)) {
                newSet.delete(userId);
            } else {
                newSet.add(userId);
            }
            return newSet;
        });
    };

    // Delete user
    const handleDeleteUser = async (userId: string) => {
        if (!window.confirm('Naozaj chcete odstrániť tohto používateľa a všetky jeho osoby?')) return;

        try {
            const response = await authenticatedFetch(
                `${process.env.REACT_APP_PATH}/api/admin/user/${userId}`,
                { method: 'DELETE' }
            );

            if (response.ok) {
                setUsers(prev => prev.filter(user => user.id !== userId));
            } else {
                alert('Chyba pri odstraňovaní používateľa');
            }
        } catch (error) {
            console.error('Error deleting user:', error);
            alert('Chyba pri odstraňovaní používateľa');
        }
    };

    // Delete person
    const handleDeletePerson = async (personId: string, userId: string) => {
        // Find user and person locally to enforce client-side guard
        const user = users.find(u => u.id === userId);
        const person = user?.person.find(p => p.id === personId);

        // If the person is a parent, don't allow deletion when any child exists for the same user
        if (person && person.type === 'PARENT') {
            const hasChild = user?.person.some(p => p.type === 'KID');
            if (hasChild) {
                // Informative message for admins
                alert('Nie je možné odstrániť rodiča, kým existujú deti priradené k tomuto účtu. Najprv odstráňte alebo prepojte deti.');
                return;
            }
        }

        if (!window.confirm('Naozaj chcete odstrániť túto osobu?')) return;

        try {
            const response = await authenticatedFetch(
                `${process.env.REACT_APP_PATH}/api/admin/person/${personId}`,
                { method: 'DELETE' }
            );

            if (response.ok) {
                setUsers(prev => prev.map(user =>
                    user.id === userId
                        ? { ...user, person: user.person.filter(p => p.id !== personId) }
                        : user
                ));
            } else {
                alert('Chyba pri odstraňovaní osoby');
            }
        } catch (error) {
            console.error('Error deleting person:', error);
            alert('Chyba pri odstraňovaní osoby');
        }
    };

    // Create user
    const handleCreateUser = async (email: string, altemail?: string) => {
        try {
            const response = await authenticatedFetch(
                `${process.env.REACT_APP_PATH}/api/admin/user`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ email, altemail })
                }
            );

            if (response.ok) {
                const result = await response.json();
                const newUser = result.data as User;
                setUsers(prev => [newUser, ...prev]);
                setIsCreateUserModalOpen(false);
                // Expand new user to show persons
                setExpandedUsers(prev => new Set(prev).add(newUser.id));
                alert('Používateľ bol úspešne vytvorený');
            } else {
                const err = await response.json();
                alert(err.error || 'Chyba pri vytváraní používateľa');
            }
        } catch (error) {
            console.error('Error creating user:', error);
            alert('Chyba pri vytváraní používateľa');
        }
    };

    // When person type is selected, open the person form modal with that type
    const openPersonFormWithType = (type: 'PARENT' | 'ADULT' | 'KID') => {
        setSelectedPersonType(type);
        setIsSelectPersonTypeOpen(false);
        setIsCreatePersonModalOpen(true);
    };

    const createEmptyPrihlaska = async (personId: string): Promise<{ id: string; status: string }> => {
        const response = await authenticatedFetch(
            `${process.env.REACT_APP_PATH}/api/admin/prihlaska`,
            {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    personId: personId,
                    kruzkyIds: [],
                    vzdelavacyPoukaz: false,
                    cestnePrehlasenie: false,
                    paymentOption: null,
                    status: 'DRAFT'
                })
            }
        );

        if (response.ok) {
            const result = await response.json();
            if (!result.data?.id) {
                throw new Error('Chýba identifikátor vytvorenej prihlášky');
            }
            return result.data;
        } else {
            const error = await response.json();
            throw new Error(error.error || 'Chyba pri vytváraní prihlášky pre osobu');
        }
    };

    const handleCreatePersonForUser = async (personData: Partial<Person>) => {
        if (!createPersonUserId) return;

        try {
            const body = { ...personData, userId: createPersonUserId };
            const response = await authenticatedFetch(
                `${process.env.REACT_APP_PATH}/api/admin/person`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(body)
                }
            );

            if (response.ok) {
                const result = await response.json();
                const newPerson = result.data as Person;



                setUsers(prev => prev.map(u => u.id === createPersonUserId ? { ...u, person: [...u.person, newPerson] } : u));
                setIsCreatePersonModalOpen(false);
                setCreatePersonUserId(null);
                setSelectedPersonType(null);

                // Update success message to indicate prihlaska creation for applicable types
                if (newPerson.type === 'KID' || newPerson.type === 'ADULT') {
                    alert('Osoba bola úspešne vytvorená!');
                } else {
                    alert('Osoba bola úspešne vytvorená');
                }
            } else {
                const err = await response.json();
                throw new Error(err.error || 'Chyba pri vytváraní osoby');
            }
        } catch (error) {
            console.error('Error creating person:', error);
            throw error;
        }
    };

    const handleCreateEmptyPrihlaska = async (person: Person) => {
        if (creatingPrihlaskaFor || person.prihlaska?.length) return;
        if (person.type === 'KID' || person.type === 'ADULT') {
            setCreatingPrihlaskaFor(person.id);
            try {
                const prihlaska = await createEmptyPrihlaska(person.id);
                setUsers(prev => prev.map(user => ({
                    ...user,
                    person: user.person.map(p => p.id === person.id ? { ...p, prihlaska: [prihlaska] } : p)
                })));
                navigate(`/admin/prihlasky?prihlaskaId=${encodeURIComponent(prihlaska.id)}`);
            } catch (error) {
                console.error('Error creating empty prihlaska:', error);
                alert(error instanceof Error ? error.message : 'Chyba pri vytváraní prihlášky pre osobu');
            } finally {
                setCreatingPrihlaskaFor(null);
            }

        }
    }

    // Edit person
    const handleEditPerson = (person: Person) => {
        setEditingPerson(person);
        setIsEditModalOpen(true);
    };

    const handleSavePerson = async (personData: Partial<Person>) => {
        if (!editingPerson) return;

        try {
            const response = await authenticatedFetch(
                `${process.env.REACT_APP_PATH}/api/admin/person/${editingPerson.id}`,
                {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(personData)
                }
            );

            if (response.ok) {
                const updatedPersonResponse = await response.json();
                const updatedPerson = updatedPersonResponse.data;

                setUsers(prev => prev.map(user =>
                    user.id === updatedPerson.userId
                        ? {
                            ...user,
                            person: user.person.map(p =>
                                p.id === updatedPerson.id ? { ...p, ...updatedPerson } : p
                            )
                        }
                        : user
                ));

                setIsEditModalOpen(false);
                setEditingPerson(null);
            } else {
                alert('Chyba pri ukladaní osoby');
            }
        } catch (error) {
            console.error('Error saving person:', error);
            alert('Chyba pri ukladaní osoby');
        }
    };

    const getPersonTypeLabel = (type: string) => {
        switch (type) {
            case 'PARENT': return 'Rodič';
            case 'ADULT': return 'Dospelý';
            case 'KID': return 'Dieťa';
            default: return type;
        }
    };

    const getGenderLabel = (gender?: string) => {
        switch (gender) {
            case 'MALE': return 'Muž';
            case 'FEMALE': return 'Žena';
            default: return 'Neuvedené';
        }
    };

    const getPrihlaskaStatusLabel = (status?: string) => {
        switch (status) {
            case 'DRAFT': return 'Koncept';
            case 'GENERATED': return 'Odoslaná';
            case 'ACCEPTED': return 'Prijatá';
            case 'REJECTED': return 'Zamietnutá';
            case 'CANCELLED': return 'Zrušená';
            default: return 'Neznámy';
        }
    };

    // helper values for create-person modal
    const selectedUserForCreate = users.find(u => u.id === createPersonUserId);
    const selectedUserHasParent = !!selectedUserForCreate?.person.some(p => p.type === 'PARENT');
    const canAddParentForSelected = !selectedUserHasParent;
    const canAddChildForSelected = selectedUserHasParent;



    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="text-lg text-gray-600">Načítavam...</div>
            </div>
        );
    }

    return (
        <div className="space-y-8">
            {/* Admins Section */}
            <Card>
                <div className="p-6">
                    <div className="flex justify-between items-center mb-4">
                        <h2 className="text-xl font-semibold text-gray-900">
                            Administrátori ({admins.length})
                        </h2>
                        <Button
                            variant="danger"
                            size="sm"
                            onClick={() => setIsCreateAdminModalOpen(true)}
                        >
                            Pridať administrátora
                        </Button>
                    </div>
                    <div className="space-y-3">
                        {admins.map((admin) => (
                            <div key={admin.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                <div>
                                    <span className="font-medium text-gray-900">{admin.username} </span>
                                    <span className="text-sm text-gray-500">{admin.role === 'VIEWER' ? 'Bernolákova (iba čítanie)' : 'Administrátor'}</span>
                                </div>
                                {admin.id !== currentAdminLoggedIn ? <Button
                                    variant="danger"
                                    size="sm"
                                    onClick={() => handleDeleteAdmin(admin.id)}
                                >
                                    Odstrániť
                                </Button> :
                                    <span className="text-sm text-gray-500 italic">Práve prihlásený</span>
                                }
                            </div>
                        ))}
                        {admins.length === 0 && (
                            <p className="text-gray-500 text-center py-4">Žiadni administrátori</p>
                        )}
                    </div>
                </div>
            </Card>

            {/* Users Section */}
            <Card>
                <div className="p-6">
                    <h2 className="text-xl font-semibold text-gray-900 mb-4">
                        Používatelia ({filteredUsers.length}{users.length !== filteredUsers.length ? ` z ${users.length}` : ''})
                        {filteredUsers.length > itemsPerPage && (
                            <span className="text-sm font-normal text-gray-600 ml-2">
                                (Strana {currentPage} z {totalPages})
                            </span>
                        )}
                    </h2>

                    {/* Search and Filters */}
                    <div className="mb-6 space-y-4">
                        {/* Search Input */}
                        <div className="flex flex-col sm:flex-row gap-4">
                            <div className="flex-1">
                                <SearchInput
                                    value={searchQuery}
                                    onChange={setSearchQuery}
                                    placeholder="Hľadať podľa mena, priezviska alebo emailu..."
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                            </div>
                            <Button variant="secondary" size="sm" onClick={() => setIsCreateUserModalOpen(true)}>
                                Vytvoriť používateľa
                            </Button>
                        </div>

                        {/* Filters */}
                        <div className="flex flex-col sm:flex-row gap-4">
                            <div className="flex-1">
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Stav používateľa
                                </label>
                                <select
                                    value={userStatusFilter}
                                    onChange={(e) => setUserStatusFilter(e.target.value as 'ALL' | 'WITH_PERSONS' | 'WITHOUT_PERSONS')}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                    <option value="ALL">Všetci používatelia</option>
                                    <option value="WITH_PERSONS">S osobami</option>
                                    <option value="WITHOUT_PERSONS">Bez osôb</option>
                                </select>
                            </div>

                            <div className="flex-1">
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Typ osoby
                                </label>
                                <select
                                    value={personTypeFilter}
                                    onChange={(e) => setPersonTypeFilter(e.target.value as 'ALL' | 'PARENT' | 'ADULT' | 'KID')}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                    <option value="ALL">Všetky typy osôb</option>
                                    <option value="PARENT">Rodičia</option>
                                    <option value="ADULT">Dospelí</option>
                                    <option value="KID">Deti</option>
                                </select>
                            </div>

                            <div className="flex-1">
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Počet na stránku
                                </label>
                                <select
                                    value={itemsPerPage}
                                    onChange={(e) => {
                                        setItemsPerPage(Number(e.target.value));
                                        setCurrentPage(1);
                                    }}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                    <option value={5}>5</option>
                                    <option value={10}>10</option>
                                    <option value={20}>20</option>
                                    <option value={50}>50</option>
                                    <option value={100}>100</option>
                                </select>
                            </div>

                            {(searchQuery || personTypeFilter !== 'ALL' || userStatusFilter !== 'ALL') && (
                                <div className="flex items-end">
                                    <Button variant="secondary" size="sm" onClick={clearFilters}>
                                        Vymazať filtre
                                    </Button>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="space-y-2">
                        {paginatedUsers.map((user) => {
                            const isExpanded = expandedUsers.has(user.id);
                            const firstPerson = user.person.length > 0 ? user.person[0] : null;
                            const displayName = firstPerson ? `${firstPerson.lastName} ${firstPerson.firstName}` : 'Bez osôb';

                            return (
                                <div key={user.id} className="border border-gray-200 rounded-lg p-3 hover:bg-gray-50 transition-colors">
                                    <div className="flex items-center justify-between">
                                        <div className="flex-1 cursor-pointer py-1" onClick={() => toggleUserExpansion(user.id)}>
                                            <div className="flex items-center space-x-3 ">
                                                <div className="flex-1">
                                                    <div className="flex items-center space-x-2">
                                                        <h3 className="font-medium text-gray-900">{displayName}</h3>
                                                        <span className="text-sm text-gray-500">({user.email})</span>
                                                    </div>
                                                    <div className="flex items-center space-x-2 mt-1">
                                                        <span className="text-m text-gray-500">{user.person.length} osôb</span>
                                                        {user.altemail && <span className="text-sm text-gray-400">• Alt: {user.altemail}</span>}
                                                    </div>
                                                </div>
                                                <svg className={`w-4 h-4  text-gray-400 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                                </svg>
                                            </div>
                                        </div>

                                        <div className="flex items-center space-x-2 pl-8">
                                            <Button variant="success" size="sm" onClick={() => { setCreatePersonUserId(user.id); setIsSelectPersonTypeOpen(true); }}>Pridať osobu</Button>
                                            <Button variant="danger" size="sm" onClick={() => handleDeleteUser(user.id)}>Odstrániť</Button>
                                        </div>
                                    </div>

                                    {isExpanded && (
                                        <div className="mt-3 pt-3 border-t border-gray-100">
                                            <h4 className="font-medium text-gray-700 mb-2 text-sm">Osoby ({user.person.length})</h4>
                                            <div className="space-y-2">
                                                {user.person.map((person) => (
                                                    <div key={person.id} className="bg-gray-50 p-3 rounded border">
                                                        <div className="flex items-center justify-between">
                                                            <div className="flex-1">
                                                                <div className="flex items-center space-x-4">
                                                                    <span className="font-medium">{person.lastName} {person.firstName}</span>
                                                                    <span className="text-sm text-gray-600">{getPersonTypeLabel(person.type)}</span>
                                                                    <span className="text-sm text-gray-600">{getGenderLabel(person.gender)}</span>
                                                                </div>
                                                                <div className="text-sm text-gray-600 mt-1">
                                                                    <span>{person.email}</span>
                                                                    {person.phone && <span> • {person.phone}</span>}
                                                                    {person.rodCislo && <span> • {person.rodCislo}</span>}
                                                                </div>
                                                                {person.address && <div className="text-sm text-gray-600">{person.address} {person.houseNumber}, {person.city} {person.psc}</div>}
                                                                {person.schoolyear && <div className="text-sm text-gray-600">Ročník: {person.schoolyear.name}</div>}
                                                            </div>
                                                            <div className="flex space-x-2">
                                                                <Button variant="secondary" size="sm" onClick={() => handleEditPerson(person)}>Upraviť</Button>
                                                                <Button variant="danger" size="sm" onClick={() => handleDeletePerson(person.id, user.id)}>Odstrániť</Button>
                                                                {person.type === 'KID' || person.type === 'ADULT' ? (
                                                                    (!person.prihlaska || person.prihlaska.length === 0) ? (
                                                                        <Button variant="success" size="sm" disabled={creatingPrihlaskaFor !== null} onClick={() => handleCreateEmptyPrihlaska(person)}>Vytvoriť prihlášku</Button>
                                                                    ) : (
                                                                        <Link
                                                                            className="text-sm text-blue-600 font-medium px-2 py-1 bg-blue-50 rounded hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                                            to={`/admin/prihlasky?prihlaskaId=${encodeURIComponent(person.prihlaska[0].id)}`}
                                                                        >
                                                                            Má prihlášku ({getPrihlaskaStatusLabel(person.prihlaska[0]?.status)})
                                                                        </Link>
                                                                    )
                                                                ) : null}
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                                {user.person.length === 0 && <p className="text-gray-500 text-sm italic">Žiadne osoby</p>}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                        {filteredUsers.length === 0 && users.length > 0 && (
                            <p className="text-gray-500 text-center py-8">Žiadni používatelia nevyhovujú filtrom</p>
                        )}
                        {users.length === 0 && (
                            <p className="text-gray-500 text-center py-8">Žiadni používatelia</p>
                        )}
                    </div>

                    {/* Pagination Controls */}
                    {totalPages > 1 && (
                        <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-4 pt-4 border-t border-gray-200">
                            <div className="text-sm text-gray-700">
                                Zobrazuje sa {startIndex + 1}-{Math.min(endIndex, filteredUsers.length)} z {filteredUsers.length} používateľov
                            </div>

                            <div className="flex items-center space-x-2">
                                <Button
                                    variant="secondary"
                                    size="sm"
                                    onClick={goToPrevPage}
                                    disabled={currentPage === 1}
                                >
                                    Predchádzajúca
                                </Button>

                                <div className="flex items-center space-x-1">
                                    {(() => {
                                        const pages = [];
                                        const maxVisiblePages = 5;

                                        if (totalPages <= maxVisiblePages) {
                                            // Show all pages if total is small
                                            for (let i = 1; i <= totalPages; i++) {
                                                pages.push(i);
                                            }
                                        } else {
                                            // Show pages around current page
                                            const halfVisible = Math.floor(maxVisiblePages / 2);
                                            let startPage = Math.max(1, currentPage - halfVisible);
                                            let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

                                            // Adjust if we're near the end
                                            if (endPage - startPage + 1 < maxVisiblePages) {
                                                startPage = Math.max(1, endPage - maxVisiblePages + 1);
                                            }

                                            for (let i = startPage; i <= endPage; i++) {
                                                pages.push(i);
                                            }
                                        }

                                        return pages.map(pageNum => (
                                            <button
                                                key={pageNum}
                                                onClick={() => goToPage(pageNum)}
                                                className={`px-3 py-1 text-sm rounded ${currentPage === pageNum
                                                    ? 'bg-blue-500 text-white'
                                                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                                    }`}
                                            >
                                                {pageNum}
                                            </button>
                                        ));
                                    })()}
                                </div>

                                <Button
                                    variant="secondary"
                                    size="sm"
                                    onClick={goToNextPage}
                                    disabled={currentPage === totalPages}
                                >
                                    Nasledujúca
                                </Button>
                            </div>
                        </div>
                    )}
                </div>
            </Card>

            {/* Edit Person Modal */}
            <EditPersonModal
                person={editingPerson}
                isOpen={isEditModalOpen}
                onClose={() => {
                    setIsEditModalOpen(false);
                    setEditingPerson(null);
                }}
                onSave={handleSavePerson}
            />

            {/* Create Admin Modal */}
            <CreateAdminModal
                isOpen={isCreateAdminModalOpen}
                onClose={() => setIsCreateAdminModalOpen(false)}
                onSave={handleCreateAdmin}
            />

            {/* Create User Modal */}
            <Modal isOpen={isCreateUserModalOpen} onClose={() => setIsCreateUserModalOpen(false)} title="Vytvoriť používateľa">
                <CreateUserForm onCancel={() => setIsCreateUserModalOpen(false)} onCreate={handleCreateUser} />
            </Modal>

            {/* Select Person Type Modal */}
            <Modal isOpen={isSelectPersonTypeOpen} onClose={() => { setIsSelectPersonTypeOpen(false); setCreatePersonUserId(null); }} title="Vyberte typ osoby">
                <div className="space-y-4">
                    {(() => {
                        const user = selectedUserForCreate;
                        return (
                            <div className="flex flex-col space-y-3">
                                <p className="text-sm text-gray-600">Vyberte typ osoby, ktorú chcete pridať pre používateľa <strong>{user?.email}</strong></p>
                                <div className="flex space-x-2 justify-center">
                                    <Button variant="primary" onClick={() => openPersonFormWithType('ADULT')}>Dospelý</Button>
                                    {canAddParentForSelected && <Button variant="primary" onClick={() => openPersonFormWithType('PARENT')}>Rodič</Button>}
                                    {canAddChildForSelected && <Button variant="primary" onClick={() => openPersonFormWithType('KID')}>Dieťa</Button>}
                                </div>
                            </div>
                        );
                    })()}
                </div>
            </Modal>

            {/* Create Person Modal (for existing user) */}
            <Modal isOpen={isCreatePersonModalOpen} onClose={() => { setIsCreatePersonModalOpen(false); setCreatePersonUserId(null); }} title="Pridať osobu">
                <div className="max-w-4xl mx-auto">
                    <PersonForm
                        onSubmit={async (data) => {
                            await handleCreatePersonForUser(data as Partial<Person>);
                        }}
                        onCancel={() => { setIsCreatePersonModalOpen(false); setCreatePersonUserId(null); setSelectedPersonType(null); }}
                        isEmbedded={true}
                        title="Nová osoba"
                        personType={selectedPersonType || 'ADULT'}
                        userEmail={users.find(u => u.id === createPersonUserId)?.email}
                    />
                </div>
            </Modal>

        </div>
    );
};

// CreateUserForm component
const CreateUserForm: React.FC<{ onCreate: (email: string, altemail?: string) => void; onCancel: () => void }> = ({ onCreate, onCancel }) => {
    const [email, setEmail] = useState('');
    const [altemail, setAltemail] = useState('');

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!email) return alert('Zadajte email');
        onCreate(email.trim(), altemail.trim() || undefined);
        setEmail('');
        setAltemail('');
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input value={email} onChange={e => setEmail(e.target.value)} type="email" className="w-full px-3 py-2 border border-gray-300 rounded" required />
            </div>
            <div className="flex justify-end space-x-2 pt-4">
                <Button variant="secondary" type="button" onClick={onCancel}>Zrušiť</Button>
                <Button variant="primary" type="submit">Vytvoriť</Button>
            </div>
        </form>
    );
};

export default UserManagement;
